import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronLeft, History } from 'lucide-react'
import { useState } from 'react'
import { productsQuery, stockCardQuery, type LedgerEntry } from '@/api/goods'
import { DocumentByIdPanel } from '@/app/goods/document-panel'
import { MoreFooter } from '@/app/goods/more-footer'
import { ShelfFilter } from '@/app/goods/shelf'
import { useGoodsActions } from '@/app/goods/use-goods-actions'
import { usePaperNames } from '@/app/goods/use-paper-names'
import { useShelfFilter } from '@/app/goods/use-shelf'
import { NothingForRole } from '@/app/nothing-for-role'
import { errorText } from '@/domain/errors'
import { markupPercent, signedQty, stockState } from '@/domain/goods'
import { goodsErrorText } from '@/domain/goods-errors'
import { paperTitle, whenText } from '@/domain/goods-text'
import { formatMoney } from '@/domain/pass-rules'
import { clubSearch, shelfFromKey, shelfKey } from '@/domain/shelves'
import { t } from '@/i18n'
import { cn } from '@/lib/cn'
import { DataList, type DataColumn } from '@/ui/data-list'
import { InlineBanner } from '@/ui/inline-banner'
import { EmptyState, Page, PageHeader } from '@/ui/page'
import { SectionHeader } from '@/ui/section-header'
import { LoadingBlock } from '@/ui/spinner'
import { StatTile, StatTiles } from '@/ui/stat-tile'

/// `club` — the shelf the card is counted on, as on the list it was opened
/// from (domain/shelves.ts).
export const Route = createFileRoute('/_app/c/$orgId/goods/$productId')({
  validateSearch: (raw: Record<string, unknown>): { club?: string } => clubSearch(raw['club']),
  component: ProductCard,
})

const c = () => t.goods.card

/// A product's stock card: what it is, what is left and what it is worth,
/// and every movement with the paper behind it and the balance after it —
/// the on-hand figure explained line by line (specs/shop.md §2.5, S11).
function ProductCard() {
  const { orgId, productId } = Route.useParams()
  const { club } = Route.useSearch()
  const navigate = Route.useNavigate()
  const shelf = shelfFromKey(club)
  const goods = useGoodsActions(orgId, { productId, shelf })
  const { can } = goods
  const shelves = useShelfFilter(orgId, goods.access, can.see)
  const names = usePaperNames(orgId, can.papers)
  // The facts and the card on ONE shelf, so the newest balance is the
  // on-hand above it (S11).
  const products = useQuery({ ...productsQuery(orgId, shelf), enabled: can.see, placeholderData: keepPreviousData })
  const card = useInfiniteQuery({
    ...stockCardQuery(orgId, productId, shelf),
    enabled: can.see && can.papers,
    placeholderData: keepPreviousData,
  })
  const [paperId, setPaperId] = useState<string | null>(null)

  if (!can.see) return <NothingForRole />

  const back = (
    <Link
      to="/c/$orgId/goods"
      params={{ orgId }}
      search={club ? { club } : {}}
      className="-ml-1.5 inline-flex min-h-8 items-center gap-1 rounded-[8px] pr-2 font-medium text-text-2 hover:text-text"
    >
      <ChevronLeft aria-hidden="true" className="size-4" />
      {c().back}
    </Link>
  )

  if (products.isPending) return <LoadingBlock label={t.app.loading} />
  // A failed request is not «no such product» — say what failed, offer a retry.
  if (products.isError) {
    return (
      <Page>
        <PageHeader eyebrow={back} title={t.goods.title} />
        <InlineBanner
          kind="error"
          message={errorText(products.error, goodsErrorText)}
          action={{ label: t.app.retry, onClick: () => void products.refetch() }}
        />
      </Page>
    )
  }
  const product = products.data.find((p) => p.id === productId)
  if (!product) {
    return (
      <Page>
        <PageHeader eyebrow={back} title={t.goods.title} />
        <EmptyState icon={<History />} title={c().notFound} />
      </Page>
    )
  }

  const state = stockState(product)
  const markup = markupPercent(product.priceMinor, product.lastCostMinor)
  const entries = (card.data?.pages ?? []).flatMap((page) => page.movements)

  // The operation first — it names the row; then its date. The balance sits
  // under the movement on a phone, the way a bank statement reads.
  const columns: readonly DataColumn<LedgerEntry>[] = [
    { key: 'what', header: c().columns.what, width: 'minmax(0,2fr)', mobile: 'title', cell: (e) => paperTitle(e) },
    { key: 'date', header: c().columns.date, width: 'minmax(140px,0.9fr)', mobile: 'subtitle', cell: (e) => <span className="tabular">{whenText(e.occurredAt)}</span> },
    ...(shelf.kind === 'all' && names.clubs > 0
      ? [
          {
            key: 'club',
            header: t.goods.documents.columns.club,
            width: 'minmax(0,0.9fr)',
            hideBelow: 'lg',
            mobile: 'hidden',
            cell: (e: LedgerEntry) => names.club(e.branchId),
          } satisfies DataColumn<LedgerEntry>,
        ]
      : []),
    {
      key: 'qty',
      header: c().columns.qty,
      width: 'minmax(90px,0.6fr)',
      numeric: true,
      mobile: 'trailing',
      cell: (e) => {
        const n = signedQty(e.kind, e.qty)
        return <span className={cn('font-semibold', n > 0 ? 'text-success' : 'text-text')}>{n > 0 ? `+${n}` : `−${-n}`}</span>
      },
    },
    {
      key: 'balance',
      header: c().columns.balance,
      width: 'minmax(96px,0.6fr)',
      numeric: true,
      mobile: 'trailing',
      cell: (e) => (
        <span className={cn(e.balanceAfter < 0 ? 'text-danger' : 'text-text-2')}>
          <span className="sr-only">{c().columns.balance}: </span>
          {e.balanceAfter} {product.unit}
        </span>
      ),
    },
  ]

  return (
    <Page wide>
      <PageHeader
        eyebrow={back}
        title={product.name}
        meta={product.status === 'retired' ? t.goods.stock.state['retired'] : undefined}
        subtitle={[product.category, product.barcode ? `${c().barcode} ${product.barcode}` : null].filter(Boolean).join(' · ')}
        actions={goods.actionsFor(product)}
      />
      {goods.news ? <InlineBanner kind="done" className="mb-6" message={goods.news} /> : null}

      {shelves.options.length > 0 ? (
        <div className="mb-5">
          <ShelfFilter
            filter={shelves}
            value={shelf}
            onChange={(next) => void navigate({ search: next.kind === 'all' ? {} : { club: shelfKey(next) }, replace: true })}
          />
        </div>
      ) : null}

      {/* Three across: a count with its unit, or a sum, never cut short. */}
      <StatTiles className="mb-8 grid-cols-2 sm:grid-cols-3">
        <StatTile
          compact
          label={c().onHand}
          value={`${product.onHand} ${product.unit}`}
          tone={state === 'out' ? 'danger' : state === 'low' ? 'warning' : 'normal'}
        />
        <StatTile compact label={c().min} value={product.minStock == null ? c().noMin : `${product.minStock} ${product.unit}`} />
        <StatTile compact label={c().price} value={formatMoney(product.priceMinor)} />
        {/* What the goods cost is `stock ≥ view` (specs/shop.md §4). */}
        {can.costs ? (
          <>
            <StatTile compact label={c().cost} value={product.lastCostMinor == null ? c().unknown : formatMoney(product.lastCostMinor)} />
            <StatTile compact label={c().margin} value={markup === null ? c().unknown : c().marginValue(markup)} />
            <StatTile
              compact
              label={c().value}
              value={
                product.lastCostMinor == null
                  ? c().unknown
                  : t.goods.stock.summary.approx(formatMoney(Math.max(product.onHand, 0) * product.lastCostMinor))
              }
            />
          </>
        ) : null}
      </StatTiles>

      {can.papers ? (
        <section className="flex flex-col gap-2">
          <SectionHeader as="h2" title={c().history} inset />
          {card.isPending ? (
            <LoadingBlock label={t.app.loading} />
          ) : card.isError ? (
            <InlineBanner kind="error" message={errorText(card.error, goodsErrorText)} action={{ label: t.app.retry, onClick: () => void card.refetch() }} />
          ) : entries.length === 0 ? (
            <EmptyState icon={<History />} title={c().empty} message={can.receive ? c().emptyMessage : undefined} />
          ) : (
            <DataList
              label={c().history}
              columns={columns}
              rows={entries}
              rowKey={(e) => e.id}
              // A line opens the paper it is on.
              rowTarget={(e) => ({ onClick: () => setPaperId(e.documentId) })}
              rowLabel={(e) => `${paperTitle(e)}, ${whenText(e.occurredAt)}`}
              className={cn('transition-opacity', card.isPlaceholderData && 'opacity-60')}
              footer={
                <MoreFooter
                  shown={entries.length}
                  pages={card.data?.pages.length ?? 0}
                  hasMore={card.hasNextPage}
                  loading={card.isFetchingNextPage}
                  onMore={() => void card.fetchNextPage()}
                />
              }
            />
          )}
        </section>
      ) : null}

      {paperId ? (
        <DocumentByIdPanel
          orgId={orgId}
          documentId={paperId}
          products={new Map((products.data ?? []).map((p) => [p.id, p]))}
          names={names}
          onClose={() => setPaperId(null)}
          correction={goods.correction}
        />
      ) : null}
      {goods.panel}
    </Page>
  )
}
